import type { AxiosError } from 'axios'
import http from './http'
import {
  Configuration,
  SignatureProcessAPIApi,
  type SignatureProcessAPIApiAddDocumentRequest,
  type AcceptAndSignRequest,
  type DeleteDocumentsRequest,
  type StartSignProcessRequest,
  type StartSignProcessDto,
  type AccessTokenInfoDto,
  type SignCodeStatusDto,
  type ProcessDtoLite,
  type DocumentDto,
  type ProcessDto,
} from '@cencert/mob-signature'
import { getToken, getProcessId } from './session'
import { saveAs } from 'file-saver'
import { getConfig } from '../../config'

export type ApiCallbacks<T> = {
  onSuccess?: (data: T) => void
  onError?: (error: ErrorResponse) => void
  onFinally?: () => void
}

const handleApiCall = async <T>(
  apiCall: () => Promise<T>,
  callbacks?: ApiCallbacks<T>
): Promise<T> => {
  try {
    const result = await apiCall()
    callbacks?.onSuccess?.(result)
    return result
  } catch (error) {
    const errorResponse = error as ErrorResponse
    callbacks?.onError?.(errorResponse)
    if (!callbacks?.onError) {
      throw error
    }
    return Promise.reject(error)
  } finally {
    callbacks?.onFinally?.()
  }
}

// API Configuration
const createApi = () => {
  const config = getConfig()

  return new SignatureProcessAPIApi(
    new Configuration({
      basePath: config.apiBaseUrl,
      accessToken: async () => getToken() ?? '',
    }),
    undefined,
    http
  )
}

const getApi = () => createApi()

export const checkAccessToken = async (
  token: string,
  callbacks?: ApiCallbacks<AccessTokenInfoDto>
) => {
  return handleApiCall(async () => {
    const response = await getApi().checkAccessToken({
      checkAccessTokenRequest: { token },
    })
    return response.data
  }, callbacks)
}

export const checkSignCode = async (
  code: string,
  callbacks?: ApiCallbacks<SignCodeStatusDto>
) => {
  return handleApiCall(async () => {
    const response = await getApi().checkSignCode({
      checkSignCodeRequest: { code },
    })
    return response.data
  }, callbacks)
}

export const checkSignProcessStatus = async (
  processId?: string,
  callbacks?: ApiCallbacks<ProcessDtoLite>
) => {
  return handleApiCall(async () => {
    const id = processId ?? getProcessId()
    if (!id) throw new Error('No process ID available')

    const response = await getApi().checkSignProcessStatus({
      checkSignProcessStatusRequest: { processId: id },
    })
    return response.data
  }, callbacks)
}

export const getDocuments = async (
  processId?: string,
  callbacks?: ApiCallbacks<DocumentDto[]>
) => {
  return handleApiCall(async () => {
    const id = processId ?? getProcessId()
    if (!id) throw new Error('No process ID available')

    const response = await getApi().getDocuments({
      getDocumentsRequest: { processId: id },
    })
    return response.data
  }, callbacks)
}

export const getProcessById = async (
  processId?: string,
  callbacks?: ApiCallbacks<ProcessDto>
) => {
  return handleApiCall(async () => {
    const id = processId ?? getProcessId()
    if (!id) throw new Error('No process ID available')

    const response = await getApi().getProcessById({
      getProcessByIdRequest: { processId: id },
    })
    return response.data
  }, callbacks)
}

export const getProcessByReturnToken = async (
  returnToken: string,
  callbacks?: ApiCallbacks<ProcessDto>
) => {
  return handleApiCall(async () => {
    const response = await getApi().getProcessByReturnToken({
      getProcessByReturnTokenRequest: { returnToken },
    })
    return response.data
  }, callbacks)
}

export const getSignedDocuments = async (
  processId?: string,
  callbacks?: ApiCallbacks<void>
) => {
  return handleApiCall(async () => {
    const id = processId ?? getProcessId()
    if (!id) throw new Error('No process ID available')

    const response = await getApi().getSignedDocuments({
      getSignedDocumentsRequest: { processId: id },
    })
    return response.data
  }, callbacks)
}

export const acceptAndSign = async (
  request: AcceptAndSignRequest,
  callbacks?: ApiCallbacks<void>
) => {
  return handleApiCall(async () => {
    const response = await getApi().acceptAndSign({
      acceptAndSignRequest: request,
    })
    return response.data
  }, callbacks)
}

export const addDocument = async (
  requestData: SignatureProcessAPIApiAddDocumentRequest,
  callbacks?: ApiCallbacks<DocumentDto>
) => {
  return handleApiCall(async () => {
    const response = await getApi().addDocument(requestData)
    return response.data
  }, callbacks)
}

export const deleteDocuments = async (
  request: DeleteDocumentsRequest,
  callbacks?: ApiCallbacks<void>
) => {
  return handleApiCall(async () => {
    const response = await getApi().deleteDocuments({
      deleteDocumentsRequest: request,
    })
    return response.data
  }, callbacks)
}

export const startSignProcess = async (
  request: StartSignProcessRequest,
  callbacks?: ApiCallbacks<StartSignProcessDto>
) => {
  return handleApiCall(async () => {
    const response = await getApi().startSignProcess({
      startSignProcessRequest: request,
    })
    return response.data
  }, callbacks)
}

export function getFilenameFromHeaders(
  headers: Record<string, string | string[]>
): string {
  const contentDisposition = (headers['content-disposition'] as string) || ''

  const encodedFilenameHeaderRegex = /filename\*=(?:UTF-8'[^']*')?([^;"]+)/i
  const filenameHeaderRegex = /filename="([^"]+)"/i

  const filenameStarMatch = contentDisposition.match(encodedFilenameHeaderRegex)
  if (filenameStarMatch?.[1]) {
    return decodeURIComponent(filenameStarMatch[1].trim().replace(/^"|"$/g, ''))
  }

  const filenameMatch = contentDisposition.match(filenameHeaderRegex)
  if (filenameMatch?.[1]) {
    return filenameMatch[1].trim().replace(/^"|"$/g, '')
  }

  return 'untitled'
}

export const downloadSignedDocuments = async (
  processId: string,
  callbacks?: ApiCallbacks<void>
) => {
  return handleApiCall(async () => {
    const response = await getApi().getSignedDocuments(
      {
        getSignedDocumentsRequest: { processId },
      },
      {
        responseType: 'blob',
      }
    )

    const filename = getFilenameFromHeaders(response.headers)

    saveAs(response.data as unknown as Blob, filename)
  }, callbacks)
}

export const consumeAccessToken = async (
  token: string,
  callbacks?: ApiCallbacks<ProcessDto>
) => {
  return handleApiCall(async () => {
    const response = await getApi().consumeAccessToken({
      consumeAccessTokenRequest: { token },
    })
    return response.data
  }, callbacks)
}

export type ErrorResponse = AxiosError<{
  detail: string
  errors: Array<{ code: string; message: string }>
}>
